import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const InvestorCategory = ({ 
  id, 
  title, 
  description, 
  checked, 
  onChange, 
  isExpanded, 
  onToggleExpand 
}) => {
  return (
    <div className="mb-4 border rounded-lg overflow-hidden">
      <div 
        className="p-4 bg-gray-50 flex justify-between items-center cursor-pointer"
        onClick={() => onToggleExpand(id)}
      >
        <h3 className="font-medium text-primary">{title}</h3>
        <span className="text-gray-500">
          {isExpanded ? '▲' : '▼'}
        </span>
      </div>
      
      {isExpanded && (
        <div className="p-4 border-t">
          <p className="text-gray-700 mb-4 whitespace-pre-line">{description}</p>
          <div className="flex items-center">
            <input
              type="checkbox"
              id={`confirm-${id}`}
              checked={checked}
              onChange={() => onChange(id)}
              className="h-4 w-4 text-primary"
            />
            <label htmlFor={`confirm-${id}`} className="ml-2 text-gray-700">
              I confirm that I am a {title}
            </label>
          </div>
        </div>
      )}
    </div>
  );
};

const InvestorAccessModal = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  
  const categories = [
    {
      id: 'high-net-worth',
      title: 'Certified High Net Worth Individual (Article 48 of the FPO)',
      description: `I declare that I am a certified high net worth individual for the purposes of the Financial Services and Markets Act 2000 (Financial Promotion) Order 2005. I understand this means:

I had, during the last financial year, an annual income of £100,000 or more, or
I held net assets of £250,000 or more throughout the last financial year. (Excluding: your primary residence, rights under qualifying contracts of insurance, or pensions.)

I accept that I can lose my property and other assets from making investment decisions based on financial promotions that are not subject to regulation.`
    },
    {
      id: 'sophisticated',
      title: 'Self-Certified Sophisticated Investor (Article 50A of the FPO)',
      description: `I declare that I am a self-certified sophisticated investor for the purposes of the Financial Services and Markets Act 2000 (Financial Promotion) Order 2005. I understand this means:

I have been a director of a company turning over at least £1 million within the last 2 years, or
I have made more than one investment in an unlisted company in the last 2 years, or
I work or have worked in the past 2 years in a professional capacity in the private equity sector or in the provision of finance for SMEs, or
I am or have been in the past 2 years a member of a network or syndicate of business angels and have invested in at least one unlisted company.

I accept that I can lose my property and other assets from making investment decisions based on financial promotions that are not subject to regulation.`
    },
    {
      id: 'professional',
      title: 'Investment Professional (Article 19 of the FPO)',
      description: `I confirm that I am an authorised person or exempt person under the Financial Services and Markets Act 2000, or I fall within Article 19 of the Financial Promotion Order as:

A person whose ordinary activities involve them in making investments for the purpose of their business, or
A government, local authority, or international organisation, or
A director, officer or employee acting for any of the above.`
    },
    {
      id: 'existing-member',
      title: 'Existing Member of the Company (Article 43 of the FPO)',
      description: `I confirm that I am an existing shareholder or member of the company and that the communication relates only to shares or securities issued by that company.`
    }
  ];

  const handleToggleExpand = (categoryId) => {
    setExpandedCategory(expandedCategory === categoryId ? null : categoryId);
  };

  const handleCategoryChange = (categoryId) => {
    setSelectedCategory(categoryId === selectedCategory ? null : categoryId);
  };

  const handleContinue = () => {
    if (selectedCategory) {
      // Store in session storage that they've verified
      sessionStorage.setItem('investorVerified', 'true');
      sessionStorage.setItem('investorCategory', selectedCategory);
      onClose();
    }
  };

  const handleLeave = () => {
    navigate('/');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <h2 className="text-2xl font-bold text-primary mb-2">Investor Qualification</h2>
          <p className="text-gray-700 mb-6">
            Select the category that applies to you to access investor information:
          </p>
          
          <div className="space-y-4">
            {categories.map(category => (
              <InvestorCategory
                key={category.id}
                id={category.id}
                title={category.title}
                description={category.description}
                checked={selectedCategory === category.id}
                onChange={handleCategoryChange}
                isExpanded={expandedCategory === category.id}
                onToggleExpand={handleToggleExpand}
              />
            ))}
          </div>
          
          <div className="mt-6 p-4 bg-gray-100 rounded-lg">
            <h3 className="font-bold text-gray-800 mb-2">Important Disclaimers:</h3>
            <p className="text-gray-700 mb-4">
              The content you are about to access is a financial promotion and is exempt from the general restriction 
              in Section 21 of the Financial Services and Markets Act 2000.
              <br /><br />
              It has not been approved by an authorised person.
              <br /><br />
              By ticking one of the boxes above and continuing, you confirm that you qualify under one of the 
              exemptions and understand the risks involved.
            </p>
          </div>
          
          <div className="mt-6 flex flex-col sm:flex-row justify-between gap-4">
            <button
              onClick={handleLeave}
              className="px-6 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition-colors"
            >
              Return to Homepage
            </button>
            <button
              onClick={handleContinue}
              disabled={!selectedCategory}
              className={`px-6 py-2 rounded-md transition-colors ${
                selectedCategory 
                  ? 'bg-primary text-white hover:bg-primary-dark' 
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              Continue
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvestorAccessModal;